import { ApiMethodIcon } from '@/components/api-method-icon/ApiMethodIcon';
import { ApiMethodIconSize } from '@/components/api-method-icon/type/ApiMethodIconSize';
import {
  operationConfigInitialState,
  OperationConfigModelStoreProvider,
} from '@/models/operation-config/operationConfig';
import { ApiType } from '@/shared/constant/ApiType';
import type { CallbackItem, ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { ModelType } from '@tarslib/types/lib/common';
import { RequestMethodType } from '@tarslib/types/lib/httpApi';
import { Drawer } from 'aui';
import { memo, default as React } from 'react';
import { MarkdownViewer } from '../../../markdown-viewer/MarkdownViewer';
import { RequestParameter } from '../../../request-parameter/RequestParameter';
import { RequestSampleCode } from '../../../request-sample-code/RequestSampleCode';
import { ResponseParameter } from '../../../response-parameter/ResponseParameter';
import { VariablesText } from '../../../variables-text/VariablesText';

type CallbacksDetailProps = {
  open?: boolean;
  apiDetail?: CallbackItem;
  onClose?: () => void;
  name?: string;
};

function CallbacksDetailComponent({ open, apiDetail, onClose, name }: CallbacksDetailProps) {
  const environmentUrlValue = React.useMemo(() => {
    return {
      type: ModelType.HttpApi as const,
      method: apiDetail?.method ?? RequestMethodType.GET,
      path: apiDetail?.path ?? '',
      apiType: ApiType.Callback,
    };
  }, [apiDetail]);

  return (
    <Drawer destroyOnClose title={name} open={open} width="60vw" onClose={onClose}>
      {apiDetail && (
        <OperationConfigModelStoreProvider state={operationConfigInitialState}>
          <div className="p-6">
            <h1 className="mb-4 text-xl font-600 leading-10 text-color" title={name}>
              {name}
            </h1>
            <div className="mb-8 flex w-full rounded-xl border border-border-color-base bg-input p-1 shadow-sm">
              <div className="m-1 flex items-center">
                <ApiMethodIcon
                  value={environmentUrlValue}
                  className="flex h-full max-h-[90px] !rounded"
                  size={ApiMethodIconSize.Large}
                  apiLabelSize={ApiMethodIconSize.Large}
                />
              </div>
              <div className="flex min-h-[32px] w-0 flex-1 items-center break-all font-400 leading-5 text-color">
                <VariablesText
                  text={environmentUrlValue.path}
                  copyable
                  copyableText={environmentUrlValue.path}
                />
              </div>
            </div>
            <div className="mx-5 w-full">
              {apiDetail?.description && (
                <MarkdownViewer
                  value={apiDetail.description}
                  inDocView
                  enableHeaderPlugin
                  className="mb-8"
                />
              )}
            </div>
            <RequestParameter
              className="mb-8"
              value={{ ...apiDetail, type: ModelType.HttpApi, auth: apiDetail.auth }}
            />
            <RequestSampleCode
              className="mb-8"
              value={apiDetail}
              extraData={apiDetail.extraData as ResourceHttpApiData['extraData']}
            />
            <ResponseParameter key={apiDetail.id} className="mb-8" value={apiDetail} />
          </div>
        </OperationConfigModelStoreProvider>
      )}
    </Drawer>
  );
}

export const CallbacksDetail = memo(CallbacksDetailComponent);
